/**
 * Businesses using ReviewsJet, as logos.
 *
 * ---------------------------------------------------------------------------
 * Nothing goes in this list without the business's permission
 * ---------------------------------------------------------------------------
 * A logo strip is a public claim that a named company uses this product and is
 * happy to be seen saying so. That is theirs to grant rather than ours to
 * assume, and the list this file inherited from the outgoing site had never
 * asked anybody.
 *
 * The nine that came out of the inherited list are in the history along
 * with their artwork — `git log -- public/images/customers` — and can come back
 * one at a time as permission arrives. Their files were deleted rather than
 * merely unreferenced, because a PNG left under `public/` is still served to
 * anyone who knows its name.
 *
 * Two of those nine could never have been asked. `mark-11.png` and
 * `mark-14.png` were bare symbols with no wordmark, no telling filename and no
 * metadata naming the business: impossible to write to, impossible to check
 * were still customers, and impossible to defend as a claim about anyone.
 *
 * Before adding one back: get it in writing, confirm they are still a customer,
 * and note the date beside the entry. A logo for a business that has since left
 * is a false claim rather than an out-of-date one.
 *
 * ---------------------------------------------------------------------------
 * `name` is what a screen reader gets
 * ---------------------------------------------------------------------------
 * The old markup shipped `alt="aaaaa"` on five of these and `alt=""` on the
 * rest, which is worse than either choice made on purpose. Every mark that
 * remains says whose it is, so every one of them is named.
 */

export type CustomerLogo = {
  /** File under `public/images/customers/`. */
  file: string;
  /** The business, where the mark says so. Empty means "draw it, don't name it". */
  name: string;
  /** Intrinsic pixel size, so the row reserves its space before the files land. */
  width: number;
  height: number;
};

/**
 * Wide wordmarks alternate with compact marks so long logos never cluster and
 * the row keeps an even rhythm as it scrolls.
 *
 * Entries dated 2026-09-27 were added at the owner's request, from the logos
 * on each business's own website.
 */
export const CUSTOMER_LOGOS: CustomerLogo[] = [
  {
    file: "metamorphosis-by-angelo.png",
    name: "Metamorphosis by Angelo",
    width: 293,
    height: 35,
  },
  // Added 2026-09-27.
  { file: "kispa-driving.png", name: "KISPA Driving School", width: 445, height: 160 },
  {
    file: "epikdrives.png",
    name: "EpikDrives Luxury Driving Tours",
    width: 243,
    height: 46,
  },
  // Added 2026-09-27.
  { file: "regain-your-game.png", name: "Regain Your Game Physical Therapy", width: 447, height: 160 },
  // Added 2026-09-27.
  { file: "intellicasa.png", name: "IntelliCasa", width: 1252, height: 160 },
  // Added 2026-09-27.
  { file: "blanco-bleaching.png", name: "Blanco Bleaching", width: 250, height: 109 },
  // Added 2026-09-27.
  { file: "helm.png", name: "Helm", width: 495, height: 125 },
  // Added 2026-09-27.
  { file: "channel-healthcare.png", name: "Channel Healthcare", width: 428, height: 145 },
  { file: "dear-charlie.png", name: "Dear Charlie", width: 144, height: 35 },
  // Added 2026-09-27.
  { file: "mtb-raceshop.png", name: "MTB-Raceshop", width: 556, height: 160 },
  // Added 2026-09-27.
  { file: "hiraya-holidays.png", name: "Hiraya Holidays", width: 382, height: 160 },
];

export const CUSTOMER_LOGO_BASE = "/images/customers/";
